/**
 * @file Toast 全局轻提示
 * @description 窗口顶部居中消息栈；边框 + 透明模糊底；fade；zustand 驱动。
 *
 * @whenToUse 所有操作反馈：成功、失败、校验未通过、提交结果等瞬时提示。
 * @whenNotToUse 需用户确认的操作（用 Confirm / Dialog）；列表空态（用 Empty）。
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { create } from 'zustand'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ToastVariant = 'default' | 'success' | 'danger' | 'warning' | 'info'

type ToastItem = {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

type ToastState = {
  items: ToastItem[]
  push: (item: Omit<ToastItem, 'id'> & { id?: string }) => string
  dismiss: (id: string) => void
}

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (item) => {
    const id = item.id ?? `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`
    set((state) => ({
      items: [...state.items, { ...item, id, variant: item.variant ?? 'default' }],
    }))
    return id
  },
  dismiss: (id) =>
    set((state) => ({ items: state.items.filter((t) => t.id !== id) })),
}))

/** 命令式 API */
export const toast = {
  show: (title: string, options?: { description?: string; variant?: ToastVariant }) =>
    useToastStore.getState().push({
      title,
      description: options?.description,
      variant: options?.variant ?? 'default',
    }),
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'success' }),
  danger: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'danger' }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'warning' }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'info' }),
}

/** 边框 + 透明模糊底：语义色走边框与文字，背景半透明 */
const variantClass: Record<ToastVariant, string> = {
  default: 'border-foreground bg-foreground/75 text-white',
  success: 'border-success bg-success/75 text-white',
  danger: 'border-danger bg-danger/75 text-white',
  warning: 'border-warning bg-warning/75 text-white',
  info: 'border-info bg-info/75 text-white',
}

/**
 * 挂载在应用根部，渲染 Toast 栈
 */
export function ToastViewport() {
  const items = useToastStore((s) => s.items)
  const dismiss = useToastStore((s) => s.dismiss)
  const [ready, setReady] = useState(false)

  useEffect(() => setReady(true), [])

  useEffect(() => {
    if (items.length === 0) return
    const timers = items.map((item) =>
      window.setTimeout(() => dismiss(item.id), 2000),
    )
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [items, dismiss])

  if (!ready || typeof document === 'undefined') return null

  return createPortal(
    <div className="pointer-events-none fixed top-4 left-1/2 z-100 flex w-80 max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(
            'pointer-events-auto rounded-xl border px-3 py-2 text-sm backdrop-blur-md transition-opacity duration-200',
            variantClass[item.variant],
          )}
          role="status"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{item.title}</div>
              {item.description ? (
                <p className="mt-0.5 text-xs opacity-80">{item.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="关闭"
              className="mt-0.5 opacity-80 transition-opacity duration-200 hover:opacity-90"
              onClick={() => dismiss(item.id)}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  )
}
